<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

// One row per hand-off: an agent's conversations in an inbox, and who takes
// them. The same dialog serves a plain hand-off (one row), removing an agent
// from an inbox (one row per removed agent), and removing an agent from the
// account (one row per inbox they hold conversations in).
const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  note: { type: String, default: '' },
  confirmLabel: { type: String, required: true },
  type: { type: String, default: 'edit' },
  rows: { type: Array, default: () => [] },
  switchOffLabel: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();
const dialogRef = ref(null);
const targets = ref({});
const switchOff = ref(true);

const UNASSIGNED = '';

watch(
  () => props.rows,
  rows => {
    targets.value = Object.fromEntries(
      rows.map(row => [row.key, row.candidates[0]?.id ?? UNASSIGNED])
    );
  },
  { immediate: true }
);

const optionsFor = row => [
  ...row.candidates.map(agent => ({ value: agent.id, label: agent.name })),
  { value: UNASSIGNED, label: t('INBOX_MGMT.HANDOFF.LEAVE_UNASSIGNED') },
];

const countLabel = row => t('INBOX_MGMT.HANDOFF.COUNT', row.count);

const selectedTargets = computed(() =>
  Object.fromEntries(
    Object.entries(targets.value).map(([key, value]) => [
      key,
      value === UNASSIGNED ? null : value,
    ])
  )
);

const handleConfirm = () => {
  emit('confirm', {
    targets: selectedTargets.value,
    switchOff: Boolean(props.switchOffLabel) && switchOff.value,
  });
};

defineExpose({
  open: () => dialogRef.value?.open(),
  close: () => dialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :type="type"
    :title="title"
    :description="description"
    :confirm-button-label="confirmLabel"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="emit('close')"
  >
    <ul class="flex flex-col gap-3">
      <li
        v-for="row in rows"
        :key="row.key"
        class="flex items-center justify-between gap-3"
      >
        <span class="flex items-baseline gap-2 text-sm text-n-slate-12">
          <span>{{ row.label }}</span>
          <span class="text-n-slate-11">{{ countLabel(row) }}</span>
        </span>
        <Select v-model="targets[row.key]" :options="optionsFor(row)" />
      </li>
    </ul>
    <label
      v-if="switchOffLabel"
      class="flex items-start gap-2 text-sm text-n-slate-12 cursor-pointer"
    >
      <Checkbox v-model="switchOff" class="mt-0.5" />
      <span>{{ switchOffLabel }}</span>
    </label>
    <p v-if="note" class="mb-0 text-sm text-n-slate-11">{{ note }}</p>
  </Dialog>
</template>
